(() => {
  const selectors = {
    root: '[data-store-locator]',
    map: '[data-store-map]',
    status: '[data-store-map-status]',
    list: '[data-store-list]',
    searchForm: '[data-store-search-form]',
    searchInput: '[data-store-search-input]',
    searchStatus: '[data-store-search-status]',
  };

  const fallbackCenter = [51.946796, 6.2305353];
  const defaultZoom = 9;
  const maximumVisibleLocations = 6;
  const headOfficeName = 'Trots op de Achterhoek (Hoofdvestiging)';

  const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');

  const isHeadOffice = (location) =>
    cleanText(location?.name).localeCompare(headOfficeName, 'nl', { sensitivity: 'base' }) === 0;

  const moveHeadOfficeToFront = (items, getLocation = (item) => item) => {
    const headOfficeIndex = items.findIndex((item) => isHeadOffice(getLocation(item)));
    if (headOfficeIndex <= 0) return items;

    return [items[headOfficeIndex], ...items.slice(0, headOfficeIndex), ...items.slice(headOfficeIndex + 1)];
  };

  const normalizeWebsite = (value) => {
    const website = cleanText(value);
    if (!website) return '';

    try {
      const url = new URL(/^https?:\/\//i.test(website) ? website : `https://${website}`);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (_error) {
      return '';
    }
  };

  const websiteLabel = (website) => {
    try {
      return new URL(website).hostname.replace(/^www\./i, '');
    } catch (_error) {
      return website;
    }
  };

  const normalizeLocation = (location) => {
    const longitude = Number(location?.longitude);
    const latitude = Number(location?.latitude);

    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
    if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90) return null;

    const name = cleanText(location.name) || cleanText(location.locationName) || 'Verkooppunt';
    const address = cleanText(location.address);
    const postalCode = cleanText(location.postalCode);
    const city = cleanText(location.city);
    const locationLine = [postalCode, city].filter(Boolean).join(' ');

    return {
      id: cleanText(location.id) || `${longitude}-${latitude}-${name}`,
      name,
      address,
      locationLine,
      email: cleanText(location.email),
      phone: cleanText(location.phone),
      website: normalizeWebsite(location.website),
      longitude,
      latitude,
    };
  };

  const appendContactLink = (container, label, value, href) => {
    if (!value || !href) return;

    const row = document.createElement('p');
    row.className = 'trots-store-popup__contact';

    const labelElement = document.createElement('span');
    labelElement.textContent = `${label}: `;

    const link = document.createElement('a');
    link.href = href;
    link.textContent = value;
    if (/^https?:/i.test(href)) {
      link.target = '_blank';
      link.rel = 'noopener';
    }

    row.append(labelElement, link);
    container.appendChild(row);
  };

  const createPopupContent = (location) => {
    const content = document.createElement('div');
    content.className = 'trots-store-popup';

    const title = document.createElement('strong');
    title.textContent = location.name;
    content.appendChild(title);

    if (location.address) {
      const address = document.createElement('span');
      address.textContent = location.address;
      content.appendChild(address);
    }

    if (location.locationLine) {
      const city = document.createElement('span');
      city.textContent = location.locationLine;
      content.appendChild(city);
    }

    if (location.email || location.phone || location.website) {
      const contact = document.createElement('div');
      contact.className = 'trots-store-popup__contacts';

      appendContactLink(contact, 'E-mail', location.email, location.email ? `mailto:${location.email}` : '');
      appendContactLink(contact, 'Telefoon', location.phone, location.phone ? `tel:${location.phone.replace(/[^+\d]/g, '')}` : '');

      if (location.website) {
        appendContactLink(contact, 'Website', websiteLabel(location.website), location.website);
      }

      content.appendChild(contact);
    }

    return content;
  };

  const toRadians = (degrees) => (degrees * Math.PI) / 180;

  const distanceInKilometers = (origin, destination) => {
    const earthRadius = 6371;
    const latitudeDifference = toRadians(destination.latitude - origin.latitude);
    const longitudeDifference = toRadians(destination.longitude - origin.longitude);
    const latitude1 = toRadians(origin.latitude);
    const latitude2 = toRadians(destination.latitude);

    const a =
      Math.sin(latitudeDifference / 2) ** 2 +
      Math.cos(latitude1) * Math.cos(latitude2) * Math.sin(longitudeDifference / 2) ** 2;

    return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const formatDistance = (distance) => {
    if (!Number.isFinite(distance)) return '';
    if (distance < 10) return `${distance.toLocaleString('nl-NL', { maximumFractionDigits: 1 })} km`;
    return `${Math.round(distance).toLocaleString('nl-NL')} km`;
  };

  const createListItem = (location, distance, onActivate) => {
    const item = document.createElement('li');
    item.className = 'trots-info__store-item';

    const button = document.createElement('button');
    button.className = 'trots-info__store-button';
    button.type = 'button';

    const copy = document.createElement('span');
    copy.className = 'trots-info__store-copy';

    const name = document.createElement('strong');
    name.textContent = location.name;
    copy.appendChild(name);

    const addressLines = [location.address, location.locationLine].filter(Boolean);
    if (addressLines.length) {
      const address = document.createElement('span');
      address.textContent = addressLines.join(', ');
      copy.appendChild(address);
    }

    const distanceLabel = document.createElement('span');
    distanceLabel.className = 'trots-info__store-distance';
    distanceLabel.textContent = formatDistance(distance);

    const arrow = document.createElement('span');
    arrow.className = 'trots-info__store-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';

    button.append(copy, distanceLabel, arrow);
    button.addEventListener('click', onActivate);
    item.appendChild(button);

    if (location.phone || location.website) {
      const contacts = document.createElement('div');
      contacts.className = 'trots-info__store-contacts';

      if (location.phone) {
        const phoneLink = document.createElement('a');
        phoneLink.href = `tel:${location.phone.replace(/[^+\d]/g, '')}`;
        phoneLink.textContent = `Telefoon: ${location.phone}`;
        contacts.appendChild(phoneLink);
      }

      if (location.website) {
        const websiteLink = document.createElement('a');
        websiteLink.href = location.website;
        websiteLink.target = '_blank';
        websiteLink.rel = 'noopener';
        websiteLink.textContent = `Website: ${websiteLabel(location.website)}`;
        contacts.appendChild(websiteLink);
      }

      item.appendChild(contacts);
    }

    return item;
  };

  const setStatus = (element, message, isError = false) => {
    if (!element) return;
    element.textContent = message;
    element.classList.toggle('is-error', isError);
  };

  const initStoreLocator = async (root) => {
    const mapElement = root.querySelector(selectors.map);
    const statusElement = root.querySelector(selectors.status);
    const listElement = root.querySelector(selectors.list);
    const searchForm = root.querySelector(selectors.searchForm);
    const searchInput = root.querySelector(selectors.searchInput);
    const searchStatus = root.querySelector(selectors.searchStatus);
    const endpoint = cleanText(root.dataset.endpoint);
    const searchEndpoint = cleanText(root.dataset.searchEndpoint);

    if (!mapElement || !statusElement || !listElement || !endpoint) return;

    if (typeof window.L === 'undefined') {
      setStatus(statusElement, 'De kaart kan nu niet worden geladen. Probeer het later opnieuw.', true);
      setStatus(searchStatus, 'Zoeken is nu niet beschikbaar. Probeer het later opnieuw.', true);
      return;
    }

    const map = window.L.map(mapElement, {
      scrollWheelZoom: false,
      zoomControl: true,
    }).setView(fallbackCenter, defaultZoom);

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const markerIcon = window.L.divIcon({
      className: 'trots-store-marker-wrap',
      html: '<span class="trots-store-marker" aria-hidden="true"></span>',
      iconSize: [34, 44],
      iconAnchor: [17, 42],
      popupAnchor: [0, -37],
    });

    try {
      const response = await fetch(endpoint, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = await response.json();
      const locations = Array.isArray(payload?.locations)
        ? payload.locations.map(normalizeLocation).filter(Boolean)
        : [];

      if (!locations.length) {
        setStatus(statusElement, 'Er zijn op dit moment geen verkooppunten om te tonen.');
        setStatus(searchStatus, 'Er zijn op dit moment geen verkooppunten om te zoeken.');
        return;
      }

      const markerById = new Map();

      locations.forEach((location) => {
        const marker = window.L.marker([location.latitude, location.longitude], { icon: markerIcon })
          .bindPopup(createPopupContent(location))
          .addTo(map);
        markerById.set(location.id, marker);
      });

      setStatus(statusElement, `${locations.length} ${locations.length === 1 ? 'verkooppunt' : 'verkooppunten'} op de kaart.`);

      const activateLocation = (location) => {
        const marker = markerById.get(location.id);
        map.flyTo([location.latitude, location.longitude], 14, { duration: 0.65 });
        if (marker) marker.openPopup();
        mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };

      const renderLocationList = (items) => {
        listElement.replaceChildren();
        items.forEach(({ location, distance }) => {
          listElement.appendChild(
            createListItem(location, distance, () => activateLocation(location))
          );
        });
        listElement.hidden = items.length === 0;
      };

      const defaultLocations = moveHeadOfficeToFront(
        [...locations].sort((first, second) => first.name.localeCompare(second.name, 'nl'))
      )
        .slice(0, maximumVisibleLocations)
        .map((location) => ({ location, distance: Number.NaN }));

      renderLocationList(defaultLocations);
      setStatus(
        searchStatus,
        `${defaultLocations.length} ${defaultLocations.length === 1 ? 'verkooppunt' : 'verkooppunten'} beschikbaar. Zoek op postcode of plaats voor de winkels bij jou in de buurt.`
      );

      if (!searchForm || !searchInput || !searchEndpoint) return;

      const submitButton = searchForm.querySelector('button[type="submit"]');
      const runSearch = async (query) => {
        if (query.length < 2) {
          setStatus(searchStatus, 'Vul minimaal twee tekens van een postcode of plaatsnaam in.', true);
          searchInput.focus();
          return;
        }

        if (submitButton) submitButton.disabled = true;
        setStatus(searchStatus, 'We zoeken de dichtstbijzijnde winkels…');

        try {
          const searchResponse = await fetch(`${searchEndpoint}?q=${encodeURIComponent(query)}`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (!searchResponse.ok) throw new Error(`HTTP ${searchResponse.status}`);

          const searchPayload = await searchResponse.json();
          const origin = {
            latitude: Number(searchPayload?.location?.latitude),
            longitude: Number(searchPayload?.location?.longitude),
          };

          if (!Number.isFinite(origin.latitude) || !Number.isFinite(origin.longitude)) {
            throw new Error('Geen coordinaten ontvangen');
          }

          const rankedLocations = locations
            .map((location) => ({
              location,
              distance: distanceInKilometers(origin, location),
            }))
            .sort((first, second) => first.distance - second.distance);

          const nearest = rankedLocations.slice(0, maximumVisibleLocations);

          renderLocationList(nearest);

          const nearestBounds = [
            [origin.latitude, origin.longitude],
            ...nearest.map(({ location }) => [location.latitude, location.longitude]),
          ];
          map.fitBounds(nearestBounds, { padding: [44, 44], maxZoom: 12 });

          const label = cleanText(searchPayload?.location?.label) || query;
          setStatus(
            searchStatus,
            `${nearest.length} ${nearest.length === 1 ? 'verkooppunt' : 'verkooppunten'} gevonden voor ${label}, gesorteerd op afstand.`
          );
        } catch (_error) {
          setStatus(
            searchStatus,
            'We konden deze postcode of plaats niet vinden. Controleer de invoer en probeer het opnieuw.',
            true
          );
        } finally {
          if (submitButton) submitButton.disabled = false;
        }
      };

      searchForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        await runSearch(cleanText(searchInput.value));
      });

      const initialQuery = cleanText(new URLSearchParams(window.location.search).get('location'));
      if (initialQuery) {
        searchInput.value = initialQuery;
        await runSearch(initialQuery);
        window.requestAnimationFrame(() => {
          mapElement.scrollIntoView({ behavior: 'auto', block: 'start' });
        });
      }
    } catch (_error) {
      setStatus(statusElement, 'De kaart kan nu niet worden geladen. Probeer het later opnieuw.', true);
      setStatus(searchStatus, 'Zoeken is nu niet beschikbaar. Probeer het later opnieuw.', true);
    }
  };

  const init = () => {
    document.querySelectorAll(selectors.root).forEach(initStoreLocator);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
